import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { TextId, useTextsV2 } from '../../utils/useTexts'
import { ConfigChangeMenuEntry } from '../../api/generated'
import {
  TdsBodyCell,
  TdsButton,
  TdsHeaderCell,
  TdsModal,
  TdsTable,
  TdsTableBody,
  TdsTableBodyRow,
  TdsTableHeader,
} from '@scania/tegel-react'
import {
  ConsequeceOfChangeUserChoice,
  ConsequenceOfChangeDialogData,
} from '../../types/ConsequenceOfChangeTypes'
import React from 'react'
import { TestElementTypeId } from '../../types/TestAttributeId'
import { BreakpointWidthPx } from '../../css/BreakpointWidthPx'
import { BreakpointHeightPx } from '../../css/BreakpointHeightPx'
import { CocOpenedFromType } from '../../store/types'
import {
  pushAdobeEvent,
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingConsequenceofChangeViewAllClick,
} from '../../utils/adobeAnalytics'

const AddedRemovedWrapper = styled.div`
  border-left: 3px solid var(--tds-information);
  padding-left: 24px;
  margin-bottom: 24px;
`

const MobileTable = styled(TdsTable)`
  display: none;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: table;
  }
`

const DesktopTable = styled(TdsTable)`
  display: table;

  @media screen and (max-width: ${BreakpointWidthPx.Small}px),
    screen and (max-height: ${BreakpointHeightPx.Small}px) {
    display: none;
  }
`

const OptionWrapper = styled.div``

const AddedRemovedHeader = styled.div`
  font-weight: bold;
`

const AddedRemovedText = styled.div`
  color: grey;
`

const MobileTableDividerGroup = styled(TdsBodyCell)`
  background-color: var(--tds-grey-200);
  padding: 8px !important;
  font-weight: bold;
  word-break: break-word;
`

const MobileTableDividerText = styled(TdsBodyCell)`
  padding: 8px !important;
  font-weight: bold;
  word-break: break-word;
`

const ViewAllOptionsWrapper = styled.div`
  font-size: 12px;
  font-weight: bold;
  line-height: 18px;
`

const ViewAllLink = styled.span`
  text-decoration: underline;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

interface TablePresentation {
  parentGroup: string
  guiGroup: string
  addedText: string
  removedText: string
}

interface GroupedTablePresentations {
  [key: string]: TablePresentation[]
}

interface ModalConsequenceOfChangeProps {
  data: ConsequenceOfChangeDialogData
  handleChoice: (choice: ConsequeceOfChangeUserChoice) => void
  cocOpenedFrom: CocOpenedFromType
}

export function ModalConsequenceOfChange({
  data,
  handleChoice,
  cocOpenedFrom,
}: ModalConsequenceOfChangeProps): JSX.Element {
  const t2 = useTextsV2()
  const menuTexts = data.resolvers.menuTexts
  const [tableContent, setTableContent] =
    useState<GroupedTablePresentations | null>(null)
  const [clickedAddedId, setClickedAddedId] = useState<
    string | undefined | null
  >(null)
  const [clickedRemovedId, setClickedRemovedId] = useState<
    string | undefined | null
  >(null)
  const [clickedGuiGroup, setClickedGuiGroup] = useState<
    string | undefined | null
  >(null)
  const [showViewAllLink, setShowViewAllLink] = useState<boolean>(
    cocOpenedFrom === CocOpenedFromType.guidedView ? true : false,
  )
  const [showViewLessLink, setShowViewLessLink] = useState<boolean>(false)

  const handleCancel = useCallback(() => {
    handleChoice('CANCEL')
  }, [handleChoice])

  const handleOk = useCallback(() => {
    handleChoice('OK')
  }, [handleChoice])

  const getCocContent = useCallback(() => {
    if (!menuTexts) {
      // Not initialized yet.
      console.warn('Expected menuTexts to be defined.')
      return
    }
    const getTexts = (item: ConfigChangeMenuEntry) => {
      const group = menuTexts[item.guiGroup] || item.guiGroup
      const pathArray = item.menuPath.split('/')
      // The first two items are empty. If not a full path is found, return "Unknown"
      const parentGroupId = pathArray.length > 5 ? pathArray[2] : 'Unknown'

      const parentGroup = menuTexts[parentGroupId] || parentGroupId
      const text = item.shortText || item.id.replace('~', '')
      return { group: group, parentGroup: parentGroup, text, source: item }
    }
    var cocHasItemsNotIncludedInGv = false
    const added = data.resolvers.triggerItems.map(getTexts)
    const removed = data.resolvers.deactivatedItems.map(getTexts)
    const clickedGuiGroupId = added.find(
      (item) => item.source.id === data.clickedId,
    )?.source.guiGroup
    const clickedAddedId = added.find(
      (item) => item.source.guiGroup === clickedGuiGroupId,
    )?.text
    const clickedRemovedId = removed.find(
      (item) => item.source.guiGroup === clickedGuiGroupId,
    )?.text
    const clickedGuiGroup = added.find(
      (item) => item.source.id === data.clickedId,
    )?.group

    const tablePresentations: TablePresentation[] = []
    added.forEach((item) => {
      const tableItem: TablePresentation = {
        guiGroup: item.group,
        parentGroup: item.parentGroup,
        addedText: item.text,
        removedText: '',
      }
      if (tableItem.guiGroup !== clickedGuiGroup) {
        if (
          (cocOpenedFrom === CocOpenedFromType.guidedView &&
            item.source.includedInGuidedView) ||
          cocOpenedFrom === CocOpenedFromType.allView ||
          cocOpenedFrom === CocOpenedFromType.xml
        ) {
          tablePresentations.push(tableItem)
        } else {
          cocHasItemsNotIncludedInGv = true
        }
      }
    })
    removed.forEach((item) => {
      const existingTableItemIndex = tablePresentations.findIndex(
        (tableItem) => tableItem.guiGroup === item.group,
      )
      if (existingTableItemIndex !== -1) {
        tablePresentations[existingTableItemIndex].removedText = item.text
      } else {
        const tableItem: TablePresentation = {
          guiGroup: item.group,
          parentGroup: item.parentGroup,
          addedText: '',
          removedText: item.text,
        }
        if (tableItem.guiGroup !== clickedGuiGroup) {
          if (
            (cocOpenedFrom === CocOpenedFromType.guidedView &&
              item.source.includedInGuidedView) ||
            cocOpenedFrom === CocOpenedFromType.allView ||
            cocOpenedFrom === CocOpenedFromType.xml
          ) {
            tablePresentations.push(tableItem)
          } else {
            cocHasItemsNotIncludedInGv = true
          }
        }
      }
    })
    const groupedTableContent = tablePresentations.reduce(
      (groupedResult: GroupedTablePresentations, item: TablePresentation) => {
        ;(groupedResult[item['parentGroup']] =
          groupedResult[item['parentGroup']] || []).push(item)
        return groupedResult
      },
      {},
    )
    setTableContent(groupedTableContent)
    setClickedAddedId(clickedAddedId)
    setClickedRemovedId(clickedRemovedId)
    setClickedGuiGroup(clickedGuiGroup)
    if (
      cocOpenedFrom === CocOpenedFromType.guidedView &&
      !cocHasItemsNotIncludedInGv
    ) {
      setShowViewAllLink(true)
    }
  }, [data, menuTexts, cocOpenedFrom])

  // Get content to coc-table
  useEffect(() => {
    getCocContent()
  }, [getCocContent])

  // Eventlistener to close modal on tdsClose (click outside modal and click on close cross)
  useEffect(() => {
    document.addEventListener('tdsClose', handleCancel)
    return () => {
      document.removeEventListener('tdsClose', handleCancel)
    }
  }, [handleCancel])

  const handleViewAllClick = () => {
    if (!menuTexts) {
      // Not initialized yet.
      console.warn('Expected menuTexts to be defined.')
      return
    }
    setShowViewAllLink(false)
    const getTexts = (item: ConfigChangeMenuEntry) => {
      const group = menuTexts[item.guiGroup] || item.guiGroup
      const pathArray = item.menuPath.split('/')
      // The first two items are empty. If not a full path is found, return "Unknown"
      const parentGroupId = pathArray.length > 5 ? pathArray[2] : 'Unknown'

      const parentGroup = menuTexts[parentGroupId] || parentGroupId
      const text = item.shortText || item.id.replace('~', '')
      return { group: group, parentGroup: parentGroup, text, source: item }
    }

    const added = data.resolvers.triggerItems.map(getTexts)
    const removed = data.resolvers.deactivatedItems.map(getTexts)
    const clickedGuiGroupId = added.find(
      (item) => item.source.id === data.clickedId,
    )?.source.guiGroup
    const clickedAddedId = added.find(
      (item) => item.source.guiGroup === clickedGuiGroupId,
    )?.text
    const clickedRemovedId = removed.find(
      (item) => item.source.guiGroup === clickedGuiGroupId,
    )?.text
    const clickedGuiGroup = added.find(
      (item) => item.source.id === data.clickedId,
    )?.group

    const tablePresentations: TablePresentation[] = []
    added.forEach((item) => {
      const tableItem: TablePresentation = {
        guiGroup: item.group,
        parentGroup: item.parentGroup,
        addedText: item.text,
        removedText: '',
      }
      if (tableItem.guiGroup !== clickedGuiGroup) {
        tablePresentations.push(tableItem)
      }
    })
    removed.forEach((item) => {
      const existingTableItemIndex = tablePresentations.findIndex(
        (tableItem) => tableItem.guiGroup === item.group,
      )
      if (existingTableItemIndex !== -1) {
        tablePresentations[existingTableItemIndex].removedText = item.text
      } else {
        const tableItem: TablePresentation = {
          guiGroup: item.group,
          parentGroup: item.parentGroup,
          addedText: '',
          removedText: item.text,
        }
        if (tableItem.guiGroup !== clickedGuiGroup) {
          tablePresentations.push(tableItem)
        }
      }
    })
    const groupedTableContent = tablePresentations.reduce(
      (groupedResult: GroupedTablePresentations, item: TablePresentation) => {
        ;(groupedResult[item['parentGroup']] =
          groupedResult[item['parentGroup']] || []).push(item)
        return groupedResult
      },
      {},
    )
    setTableContent(groupedTableContent)
    setClickedAddedId(clickedAddedId)
    setClickedRemovedId(clickedRemovedId)
    setClickedGuiGroup(clickedGuiGroup)
    setShowViewLessLink(true)

    const trackingEvent: ScaniaAdobeTrackingConsequenceofChangeViewAllClick = {
      event: ScaniaAdobeEventId.ConsequenceOfChangeViewAllClick,
    }
    pushAdobeEvent(trackingEvent)
  }

  const handleViewLessClick = () => {
    getCocContent()
    setShowViewAllLink(true)
    setShowViewLessLink(false)
  }

  function buildViewAllOrLessLinkSpans(view: 'all' | 'less') {
    const clickHere = t2(TextId.COC_CLICK_HERE).trim()
    const text =
      view === 'all'
        ? t2(TextId.COC_VIEW_ALL_TEXT).trim()
        : t2(TextId.COC_VIEW_LESS_TEXT).trim()
    const subStrings = text.split('{}')
    const subStringsLastIndex = subStrings.length - 1
    const nbsp = <>&nbsp;</>
    return subStrings
      .map((s) => s.trim())
      .map((s, i) => {
        const maybeNbsp = i === subStringsLastIndex ? '' : nbsp
        return s === '' ? (
          <ViewAllLink
            key={`click-here-text-${i}-${clickHere}`}
            onClick={view === 'all' ? handleViewAllClick : handleViewLessClick}
          >
            {clickHere}
            {maybeNbsp}
          </ViewAllLink>
        ) : (
          <span key={`view-all-text-${i}-${s}`}>
            {s}
            {maybeNbsp}
          </span>
        )
      })
  }
  const viewAllLinkSpans = buildViewAllOrLessLinkSpans('all')
  const viewLessLinkSpans = buildViewAllOrLessLinkSpans('less')

  return (
    <TdsModal
      size="md"
      header={
        data.showCancelButton
          ? t2(TextId.HEADER_COC)
          : t2(TextId.HEADER_COC_LOADED_TRUCK)
      }
      show
      actions-position="sticky"
      prevent={data.showCancelButton ? false : true}
    >
      <span slot="body">
        {tableContent && (
          <>
            <p style={{ marginBottom: '24px' }}>
              {data.showCancelButton
                ? t2(TextId.DESCRIPTION_COC)
                : t2(TextId.DESCRIPTION_COC_LOADED_TRUCK)}
            </p>
            {data.showCancelButton && (
              <AddedRemovedWrapper>
                <OptionWrapper style={{ marginBottom: '16px' }}>
                  <AddedRemovedHeader>
                    {t2(TextId.HEADER_COC_ADDED)}
                  </AddedRemovedHeader>
                  <AddedRemovedText>
                    {clickedGuiGroup + ' - ' + clickedAddedId}
                  </AddedRemovedText>
                </OptionWrapper>
                <OptionWrapper>
                  <AddedRemovedHeader>
                    {t2(TextId.HEADER_COC_REMOVED)}
                  </AddedRemovedHeader>
                  <AddedRemovedText>
                    {clickedGuiGroup + ' - ' + clickedRemovedId}
                  </AddedRemovedText>
                </OptionWrapper>
              </AddedRemovedWrapper>
            )}
            {data.showCancelButton && (
              <h5 style={{ marginBottom: '24px' }}>
                {t2(TextId.HEADER_COC_CHANGES)}
              </h5>
            )}

            <DesktopTable
              verticalDividers
              responsive
              noMinWidth
              style={{ marginBottom: '32px' }}
            >
              <TdsTableHeader>
                <TdsHeaderCell cellKey="ParentGroup" cellValue="" />
                <TdsHeaderCell
                  cellKey="Option"
                  cellValue={t2(TextId.HEADER_COC_TABLE_OPTION)}
                />
                <TdsHeaderCell
                  cellKey="Added"
                  cellValue={t2(TextId.HEADER_COC_TABLE_ADDED)}
                />
                <TdsHeaderCell
                  cellKey="Removed"
                  cellValue={t2(TextId.HEADER_COC_TABLE_REMOVED)}
                />
              </TdsTableHeader>
              <TdsTableBody>
                {Object.keys(tableContent)?.map((parentGroup, index) => (
                  <React.Fragment key={parentGroup + index}>
                    {tableContent[parentGroup].map((item, i) => (
                      <TdsTableBodyRow key={item.guiGroup + i}>
                        <TdsBodyCell
                          cellKey="ParentGroup"
                          cellValue={i === 0 ? item.parentGroup : ''}
                          style={{
                            fontWeight: 'bold',
                            wordBreak: 'break-word',
                          }}
                        />
                        <TdsBodyCell
                          cellKey="Option"
                          cellValue={item.guiGroup}
                          style={{ wordBreak: 'break-word' }}
                        />

                        <TdsBodyCell
                          cellKey="Added"
                          cellValue={item.addedText}
                          style={{ wordBreak: 'break-word' }}
                        />
                        <TdsBodyCell
                          cellKey="Removed"
                          cellValue={item.removedText}
                          style={{ wordBreak: 'break-word' }}
                        />
                      </TdsTableBodyRow>
                    ))}
                  </React.Fragment>
                ))}
              </TdsTableBody>
            </DesktopTable>

            <MobileTable
              verticalDividers
              responsive
              noMinWidth
              style={{ marginBottom: '32px' }}
            >
              <TdsTableHeader>
                <TdsHeaderCell
                  cellKey="Added"
                  cellValue={t2(TextId.HEADER_COC_TABLE_ADDED)}
                />
                <TdsHeaderCell
                  cellKey="Removed"
                  cellValue={t2(TextId.HEADER_COC_TABLE_REMOVED)}
                />
              </TdsTableHeader>
              <TdsTableBody>
                {Object.keys(tableContent)?.map((parentGroup, index) => (
                  <React.Fragment key={parentGroup + index}>
                    <TdsTableBodyRow>
                      <MobileTableDividerGroup
                        cellKey="ParentGroup"
                        cellValue={parentGroup}
                        disablePadding
                      />
                      <MobileTableDividerGroup
                        cellKey="Dummy1"
                        cellValue=""
                        disablePadding
                      />
                    </TdsTableBodyRow>
                    {tableContent[parentGroup].map((item, i) => (
                      <React.Fragment key={item.guiGroup + index}>
                        <TdsTableBodyRow>
                          <MobileTableDividerText
                            cellKey="ParentGroup"
                            cellValue={item.guiGroup}
                            disablePadding
                          />
                          <MobileTableDividerText
                            cellKey="Dummy2"
                            cellValue=""
                            disablePadding
                          />
                        </TdsTableBodyRow>
                        <TdsTableBodyRow>
                          <TdsBodyCell
                            cellKey="Added"
                            cellValue={item.addedText || '-'}
                            style={{ wordBreak: 'break-word' }}
                          />
                          <TdsBodyCell
                            cellKey="Removed"
                            cellValue={item.removedText || '-'}
                            style={{ wordBreak: 'break-word' }}
                          />
                        </TdsTableBodyRow>
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </TdsTableBody>
            </MobileTable>
          </>
        )}
        {showViewAllLink && (
          <ViewAllOptionsWrapper>{viewAllLinkSpans}</ViewAllOptionsWrapper>
        )}
        {showViewLessLink && (
          <ViewAllOptionsWrapper>{viewLessLinkSpans}</ViewAllOptionsWrapper>
        )}
      </span>

      <span slot="actions" className="tds-u-flex tds-u-gap2 tds-u-flex-wrap">
        <TdsButton
          size="md"
          onClick={handleOk}
          text={t2(TextId.ACTION_LABEL_ACCEPT)}
          data-test-element-type={
            TestElementTypeId.ConsequenceOfChangeAcceptButton
          }
        />
        {data?.showCancelButton && (
          <TdsButton
            variant="secondary"
            size="md"
            onClick={handleCancel}
            text={t2(TextId.LABEL_ACTION_CANCEL)}
          />
        )}
      </span>
    </TdsModal>
  )
}
